import { useRef } from 'react';
import type { BidEntry } from '@/data/auction';
import { formatIDR, formatTime } from '@/lib/format';
import { LivePulse } from '@/components/ui/primitives';

// ─── BidRow ───────────────────────────────────────────────────────────────────

interface BidRowProps {
  entry: BidEntry;
  /** Highlights this row as the current top bid */
  isLatest: boolean;
  /** Highlights this row as belonging to the current viewer */
  isMe: boolean;
}

function BidRow({ entry, isLatest, isMe }: BidRowProps) {
  return (
    <div
      className={`flex items-center gap-4 py-3 px-4 border-b border-iron-800/50 transition-colors
        ${isLatest ? 'bg-secondary/5 border-l-2 border-l-secondary' : 'hover:bg-iron-800/30'}`}
    >
      {/* Username */}
      <div className="flex-1 min-w-0">
        <span
          className={`text-sm font-bold truncate block ${
            isMe ? 'text-secondary' : 'text-iron-200'
          }`}
        >
          {entry.username}
          {isMe && (
            <span className="ml-1.5 text-[9px] tracking-widest text-secondary/70 uppercase">
              (you)
            </span>
          )}
        </span>
      </div>

      {/* Amount */}
      <span
        className={`font-epic text-sm font-bold shrink-0 ${
          isLatest ? 'text-secondary' : 'text-iron-300'
        }`}
      >
        {formatIDR(entry.amount)}
      </span>

      {/* Timestamp */}
      <span className="text-[10px] text-iron-600 tracking-wider font-mono shrink-0">
        {formatTime(entry.timestamp)}
      </span>
    </div>
  );
}

// ─── BidHistory ───────────────────────────────────────────────────────────────

interface BidHistoryProps {
  entries: BidEntry[];
  /** Username of the current viewer — used to highlight their rows */
  myUsername: string;
  /** Max height of the scrollable list (Tailwind class, default max-h-[360px]) */
  maxHeight?: string;
}

/**
 * Scrollable live bid history panel.
 *
 * - Latest bid is pinned at top with a gold accent border.
 * - Viewer's own bids are highlighted in Baratheon Gold.
 * - Shows a "LIVE" pulse indicator in the header.
 */
export function BidHistory({
  entries,
  myUsername,
  maxHeight = 'max-h-[360px]',
}: BidHistoryProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  return (
    <div className="bg-iron-900 border border-iron-800">
      {/* Header */}
      <div className="px-6 py-5 border-b border-iron-800 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-sm text-iron-200 uppercase tracking-[0.25em] font-epic">
            Bid History
          </h2>
          <span className="text-[9px] tracking-widest text-iron-600 uppercase font-bold">
            {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
          </span>
        </div>
        {/* Live pulse */}
        <div className="flex items-center gap-2">
          <LivePulse variant="gold" />
          <span className="text-[9px] tracking-widest text-secondary/60 uppercase font-bold">
            Live
          </span>
        </div>
      </div>

      {/* Rows */}
      <div ref={scrollRef} className={`${maxHeight} overflow-y-auto`}>
        {entries.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <p className="text-iron-600 font-body italic text-sm">
              No bids have been placed yet.
            </p>
          </div>
        ) : (
          entries.map((entry, idx) => (
            <BidRow
              key={entry.id}
              entry={entry}
              isLatest={idx === 0}
              isMe={entry.username === myUsername}
            />
          ))
        )}
      </div>
    </div>
  );
}
